import { useState, useEffect } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Lock, LogOut, Download, FileText, Film, Image as ImageIcon } from "lucide-react";

export default function Garage() {
  const [folder, setFolder] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const tok = localStorage.getItem("gb_portal_token");
    if (tok) {
      api.get("/garage/folder", { headers: { "X-Auth-Role": "client" } })
        .then((r) => setFolder(r.data))
        .catch(() => localStorage.removeItem("gb_portal_token"));
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/garage/login", { code });
      localStorage.setItem("gb_portal_token", data.token);
      const f = await api.get("/garage/folder", { headers: { "X-Auth-Role": "client" } });
      setFolder(f.data);
      toast.success(`Welcome, ${data.folder.client_name}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid access code.");
    } finally { setBusy(false); }
  };

  const logout = () => {
    localStorage.removeItem("gb_portal_token");
    setFolder(null);
  };

  if (!folder) {
    return (
      <main data-testid="garage-page" className="bg-black pt-32 pb-24 min-h-screen">
        <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-16 items-center min-h-[80vh]">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }} className="md:col-span-6">
            <p className="overline mb-6">Client Portal</p>
            <h1 className="font-display text-6xl md:text-8xl text-white leading-[0.95]">
              The <span className="italic text-[var(--gb-gold)]">Garage</span>
            </h1>
            <p className="text-white/60 leading-[1.8] mt-8 max-w-md">
              A private space for active Good Bandit clients. Previews, shot lists,
              draft edits and final deliverables. All in one place.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }} className="md:col-span-5 md:col-start-8">
            <div className="border border-white/10 p-10 noise">
              <Lock size={20} className="text-[var(--gb-gold)] mb-6" />
              <p className="overline mb-4">Authenticate</p>
              <h3 className="font-display text-3xl text-white mb-2">Enter your access code</h3>
              <p className="text-sm text-white/50 mb-8">Provided by your Good Bandit producer.</p>
              <form onSubmit={submit} className="space-y-6">
                <input
                  data-testid="garage-access-code"
                  value={code} onChange={(e) => setCode(e.target.value)}
                  className="gb-input font-mono-ed text-lg tracking-[0.3em]"
                  placeholder="• • • • • • • •" required
                />
                <button data-testid="garage-login-button" disabled={busy} className="gb-btn-primary w-full justify-center">
                  {busy ? "Unlocking…" : "Enter the Garage"}
                </button>
              </form>
              <p className="kbd text-white/30 mt-8">
                NEED ACCESS? EMAIL hello@thegoodbandit.com
              </p>
            </div>
          </motion.div>
        </section>
      </main>
    );
  }

  const iconFor = (cat) => {
    if (cat === "video" || cat === "draft_edit" || cat === "final_edit") return <Film size={16} />;
    if (cat === "image" || cat === "gallery") return <ImageIcon size={16} />;
    return <FileText size={16} />;
  };

  return (
    <main data-testid="garage-portal" className="bg-black pt-32 pb-24 min-h-screen">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="overline mb-3">The Garage · {folder.client_name}</p>
            <h1 className="font-display text-5xl md:text-7xl text-white leading-[0.95]">{folder.name}</h1>
            {folder.description && <p className="text-white/60 mt-4 max-w-xl">{folder.description}</p>}
          </div>
          <button onClick={logout} data-testid="garage-logout" className="gb-btn-outline">
            <LogOut size={14} /> Sign out
          </button>
        </div>

        {(folder.files || []).length === 0 ? (
          <div className="border border-white/10 p-16 text-center">
            <p className="text-white/40 font-display text-2xl">No deliverables uploaded yet.</p>
            <p className="kbd text-white/30 mt-4">YOUR PRODUCER WILL POPULATE THIS FOLDER SHORTLY.</p>
          </div>
        ) : (
          <div className="border border-white/10">
            {(folder.files || []).map((f) => (
              <div key={f.id} className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-white/5 items-center hover:bg-white/[0.02]">
                <div className="col-span-1 text-[var(--gb-gold)]">{iconFor(f.category)}</div>
                <div className="col-span-7">
                  <p className="text-white">{f.label}</p>
                  <p className="kbd text-white/40 mt-1">{f.category?.toUpperCase()}</p>
                </div>
                <div className="col-span-2 kbd text-white/40">{new Date(f.added_at).toLocaleDateString()}</div>
                <a href={f.url} target="_blank" rel="noopener noreferrer" data-testid={`garage-download-${f.id}`} className="col-span-2 gb-btn-outline text-[10px] py-2 px-4 justify-center">
                  <Download size={12} /> Download
                </a>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
